import React, { useState } from "react";
import { Pane, Heading, TextInputField, SelectField, Button, Table, Text, Paragraph, Card, Strong, Badge, Spinner, toaster } from "evergreen-ui";
import { DateFieldRange } from "../../../inputs/index.js";
import { makeDateString, getCookie } from "../../../util.js";

const RoyaltyCalculator = () => {
  const today = new Date();
  const prevMonth = new Date();
  prevMonth.setDate(prevMonth.getDate() - 30);

  const initStartDate = makeDateString(prevMonth);
  const todayDate = makeDateString(today);

  // Sample data for demonstration
  const sampleData = {
    unitsInPeriod: 75,
    retailPrice: 15.99,
    expensesInPeriod: 220,
    royaltyType: "2", // Net receipts
    payments: 50,
    tiers: [
      { threshold: 0, rate: 10, type: "2" },     // 10% of net receipts for first 100 units
      { threshold: 100, rate: 15, type: "2" },   // 15% of net receipts for units 101-200
      { threshold: 200, rate: 18, type: "2" }    // 18% of net receipts for units 201+
    ]
  };

  // State for calculation inputs
  const [startDate, setStartDate] = useState(initStartDate);
  const [endDate, setEndDate] = useState(todayDate);
  const [unitsInPeriod, setUnitsInPeriod] = useState(0);
  const [retailPrice, setRetailPrice] = useState(0);
  const [expensesInPeriod, setExpensesInPeriod] = useState(0);
  const [royaltyType, setRoyaltyType] = useState("1"); // Default to list price royalty
  const [payments, setPayments] = useState(0);
  const [tiers, setTiers] = useState([
    { threshold: 0, rate: 10, type: "1" }
  ]);

  // State for calculation results
  const [calculationResult, setCalculationResult] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);

  // Handle date changes
  const handleChangeDate = obj => {
    let newDate = makeDateString(obj.val);
    if (obj.key === "startDate") {
      setStartDate(newDate);
    } else {
      setEndDate(newDate);
    }
  };

  // Handle adding a new tier
  const handleAddTier = () => {
    const lastTier = tiers[tiers.length - 1];
    setTiers([...tiers, {
      threshold: lastTier.threshold + 100,
      rate: lastTier.rate + 2,
      type: lastTier.type
    }]);
  };

  // Handle removing a tier
  const handleRemoveTier = (index) => {
    if (tiers.length > 1) {
      const newTiers = [...tiers];
      newTiers.splice(index, 1);
      setTiers(newTiers);
    }
  };

  // Handle updating a tier
  const handleUpdateTier = (index, field, value) => {
    const newTiers = [...tiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    setTiers(newTiers);
  };

  // Load sample data
  const loadSampleData = () => {
    setUnitsInPeriod(sampleData.unitsInPeriod);
    setRetailPrice(sampleData.retailPrice);
    setExpensesInPeriod(sampleData.expensesInPeriod);
    setRoyaltyType(sampleData.royaltyType);
    setPayments(sampleData.payments);
    setTiers(sampleData.tiers);
    setCalculationResult(null);
    setShowResults(false);
  };

  // Calculate royalties using backend API
  const calculateRoyalties = async () => {
    setIsCalculating(true);

    try {
      // Sort tiers by threshold
      const sortedTiers = [...tiers].sort((a, b) => a.threshold - b.threshold);

      // Prepare request data
      const requestData = {
        startDate: startDate,
        endDate: endDate,
        unitsInPeriod: unitsInPeriod,
        retailPrice: retailPrice,
        expensesInPeriod: expensesInPeriod,
        royaltyType: parseInt(royaltyType),
        payments: payments,
        tiers: sortedTiers.map(tier => ({
          threshold: tier.threshold,
          rate: tier.rate,
          type: parseInt(tier.type)
        }))
      };

      // Call the backend API
      const csrftoken = getCookie('csrftoken');
      const response = await fetch('/api/v1/royalty_simulation/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Calculation failed');
      }

      // Process the response
      const result = await response.json();

      // Format tier breakdown for display
      const formattedTiers = result.tierBreakdown.map((tier, index) => ({
        tier: index + 1,
        thresholdStart: tier.threshold,
        thresholdEnd: tier.nextThreshold ? tier.nextThreshold - 1 : "∞",
        units: tier.unitsInTier,
        revenue: tier.revenue,
        expenses: tier.expenses,
        applicable: tier.applicable,
        rate: tier.royaltyRate,
        credit: tier.credit,
        type: tier.royaltyType.toString()
      }));

      // Set the calculation result
      setCalculationResult({
        totalCredit: result.summary.credit,
        totalDebit: result.summary.debit,
        paidAmount: result.summary.previousPayments,
        netAmount: result.summary.netAmount,
        tiers: formattedTiers,
        bottomLine: royaltyType === "4" ? 0.47 : null, // Example bottom line
        totalUnits: result.summary.units,
        totalRevenue: result.summary.totalRevenue,
        totalExpenses: result.summary.expenses,
        period: { startDate, endDate }
      });

      setShowResults(true);
    } catch (error) {
      console.error('Calculation error:', error);
      toaster.danger(`Error calculating royalties: ${error.message}`);
    } finally {
      setIsCalculating(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setUnitsInPeriod(0);
    setRetailPrice(0);
    setExpensesInPeriod(0);
    setRoyaltyType("1");
    setPayments(0);
    setTiers([{ threshold: 0, rate: 10, type: "1" }]);
    setCalculationResult(null);
    setShowResults(false);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  // Royalty type descriptions
  const royaltyTypeDescriptions = {
    "1": "% of list price: % of title's retail price, no matter how much the publisher earns",
    "2": "% of net receipts: % of publisher's income for title, not taking into account expenses",
    "3": "% of net profits: % of title's sales minus expenses",
    "4": "% of true profits/profit sharing: net profits minus publisher's bottom line percentage"
  };

  return (
    <Pane padding={16} background="tint1" minHeight="calc(100vh - 48px)">
      <Heading size={700} marginBottom={24}>Royalty Calculator</Heading>
      <Paragraph marginBottom={16}>
        This calculator uses the actual backend royalty calculation code to ensure results match production calculations.
      </Paragraph>

      <Pane display="flex" flexDirection="row">
        {/* Input form */}
        <Card
          elevation={2}
          background="white"
          padding={16}
          marginRight={16}
          width="40%"
        >
          <Pane display="flex" justifyContent="space-between" alignItems="center" marginBottom={16}>
            <Heading size={500}>Calculation Parameters</Heading>
            <Button appearance="primary" intent="success" onClick={loadSampleData} marginLeft={16}>
              Load Sample Data
            </Button>
          </Pane>

          <Pane marginBottom={16}>
            <Text>Statement Period</Text>
            <DateFieldRange
              startDate={startDate}
              endDate={endDate}
              handleChange={handleChangeDate}
            />
          </Pane>

          <Pane display="flex" marginBottom={16}>
            <TextInputField
              label="Units Sold in Period"
              placeholder="Enter units sold in period"
              value={unitsInPeriod}
              onChange={e => setUnitsInPeriod(Number(e.target.value))}
              width="48%"
              marginRight={8}
              type="number"
            />
            <TextInputField
              label="Retail Price"
              placeholder="Enter retail price"
              value={retailPrice}
              onChange={e => setRetailPrice(Number(e.target.value))}
              width="48%"
              type="number"
              step="0.01"
            />
          </Pane>

          <Pane display="flex" marginBottom={16}>
            <TextInputField
              label="Expenses in Period"
              placeholder="Enter expenses in period"
              value={expensesInPeriod}
              onChange={e => setExpensesInPeriod(Number(e.target.value))}
              width="48%"
              marginRight={8}
              type="number"
              step="0.01"
            />
            <TextInputField
              label="Previous Payments"
              placeholder="Enter payments made"
              value={payments}
              onChange={e => setPayments(Number(e.target.value))}
              width="48%"
              type="number"
              step="0.01"
            />
          </Pane>

          <SelectField
            label="Primary Royalty Type"
            value={royaltyType}
            onChange={e => setRoyaltyType(e.target.value)}
            marginBottom={16}
          >
            <option value="1">% of List Price</option>
            <option value="2">% of Net Receipts</option>
            <option value="3">% of Net Profits</option>
            <option value="4">% of True Profits</option>
          </SelectField>

          <Pane marginBottom={16}>
            <Heading size={400} marginBottom={8}>Royalty Tiers</Heading>

            {tiers.map((tier, index) => (
              <Pane key={index} display="flex" marginY={8} alignItems="center">
                <TextInputField
                  label={`Tier ${index + 1} Threshold`}
                  value={tier.threshold}
                  onChange={e => handleUpdateTier(index, 'threshold', Number(e.target.value))}
                  width="25%"
                  marginRight={8}
                  type="number"
                />
                <TextInputField
                  label={`Rate (%)`}
                  value={tier.rate}
                  onChange={e => handleUpdateTier(index, 'rate', Number(e.target.value))}
                  width="20%"
                  marginRight={8}
                  type="number"
                  step="0.1"
                />
                <SelectField
                  label={`Type`}
                  value={tier.type}
                  onChange={e => handleUpdateTier(index, 'type', e.target.value)}
                  width="35%"
                  marginRight={8}
                >
                  <option value="1">% of List Price</option>
                  <option value="2">% of Net Receipts</option>
                  <option value="3">% of Net Profits</option>
                  <option value="4">% of True Profits</option>
                </SelectField>
                {tiers.length > 1 && (
                  <Button
                    height={32}
                    iconBefore="trash"
                    intent="danger"
                    onClick={() => handleRemoveTier(index)}
                    marginTop={24}
                  />
                )}
              </Pane>
            ))}

            <Button
              iconBefore="plus"
              onClick={handleAddTier}
              marginTop={8}
            >
              Add Tier
            </Button>
          </Pane>

          <Pane display="flex" marginTop={24}>
            <Button
              appearance="primary"
              onClick={calculateRoyalties}
              marginRight={16}
              isLoading={isCalculating}
              disabled={isCalculating}
            >
              Calculate
            </Button>
            <Button onClick={handleReset} disabled={isCalculating}>
              Reset
            </Button>
          </Pane>
        </Card>

        {/* Results display */}
        {isCalculating ? (
          <Card
            elevation={2}
            background="white"
            padding={16}
            width="58%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Pane display="flex" flexDirection="column" alignItems="center">
              <Spinner size={32} marginBottom={16} />
              <Text>Calculating royalties using backend code...</Text>
            </Pane>
          </Card>
        ) : (
          showResults && calculationResult && (
            <Card
              elevation={2}
              background="white"
              padding={16}
              width="58%"
            >
              <Heading size={500} marginBottom={16}>Calculation Results</Heading>

              <Pane marginBottom={16}>
                <Paragraph size={300}>Period: {calculationResult.period.startDate} to {calculationResult.period.endDate}</Paragraph>
                <Paragraph size={300}>Units Sold in Period: {calculationResult.totalUnits}</Paragraph>
                <Paragraph size={300}>Total Revenue: {formatCurrency(calculationResult.totalRevenue)}</Paragraph>
                {(royaltyType === "3" || royaltyType === "4") && (
                  <Paragraph size={300}>Total Expenses: {formatCurrency(calculationResult.totalExpenses)}</Paragraph>
                )}
                {royaltyType === "4" && (
                  <Paragraph size={300}>Bottom Line Rate: {(calculationResult.bottomLine * 100).toFixed(1)}%</Paragraph>
                )}
              </Pane>

              <Pane marginBottom={24}>
                <Heading size={400} marginBottom={8}>Royalty Calculation Summary</Heading>
                <Pane background="#F9F9FB" padding={16} borderRadius={3}>
                  <Pane display="flex" justifyContent="space-between" marginBottom={8}>
                    <Strong>Total Credit:</Strong>
                    <Text>{formatCurrency(calculationResult.totalCredit)}</Text>
                  </Pane>
                  <Pane display="flex" justifyContent="space-between" marginBottom={8}>
                    <Strong>Total Debit:</Strong>
                    <Text>{formatCurrency(calculationResult.totalDebit)}</Text>
                  </Pane>
                  <Pane display="flex" justifyContent="space-between" marginBottom={8}>
                    <Strong>Previously Paid:</Strong>
                    <Text>{formatCurrency(calculationResult.paidAmount)}</Text>
                  </Pane>
                  <Pane display="flex" justifyContent="space-between">
                    <Strong>Net Amount Due:</Strong>
                    <Text size={500} color={calculationResult.netAmount >= 0 ? "green" : "red"}>
                      {formatCurrency(Math.abs(calculationResult.netAmount))}
                      {calculationResult.netAmount < 0 ? " (credit)" : ""}
                    </Text>
                  </Pane>
                </Pane>
              </Pane>

              <Heading size={400} marginBottom={8}>Detailed Tier Calculations</Heading>
              <Table>
                <Table.Head>
                  <Table.TextHeaderCell>Tier</Table.TextHeaderCell>
                  <Table.TextHeaderCell>Units Range</Table.TextHeaderCell>
                  <Table.TextHeaderCell>Royalty Type</Table.TextHeaderCell>
                  <Table.TextHeaderCell>Units</Table.TextHeaderCell>
                  <Table.TextHeaderCell>Revenue</Table.TextHeaderCell>
                  {(royaltyType === "3" || royaltyType === "4") && (
                    <Table.TextHeaderCell>Expenses</Table.TextHeaderCell>
                  )}
                  {royaltyType === "4" && (
                    <Table.TextHeaderCell>Adj. Profit</Table.TextHeaderCell>
                  )}
                  <Table.TextHeaderCell>Rate</Table.TextHeaderCell>
                  <Table.TextHeaderCell>Amount</Table.TextHeaderCell>
                </Table.Head>
                <Table.Body>
                  {calculationResult.tiers.map((tier, index) => (
                    <Table.Row key={index} height="auto">
                      <Table.TextCell>{index + 1}</Table.TextCell>
                      <Table.TextCell>{tier.thresholdStart} - {tier.thresholdEnd}</Table.TextCell>
                      <Table.TextCell>
                        <Badge color={
                          tier.type === "1" ? "blue" :
                          tier.type === "2" ? "green" :
                          tier.type === "3" ? "orange" :
                          "purple"
                        }>
                          {tier.type === "1" ? "List Price" :
                           tier.type === "2" ? "Net Receipts" :
                           tier.type === "3" ? "Net Profits" :
                           "True Profits"}
                        </Badge>
                      </Table.TextCell>
                      <Table.TextCell>{tier.units}</Table.TextCell>
                      <Table.TextCell>{formatCurrency(tier.revenue)}</Table.TextCell>
                      {(royaltyType === "3" || royaltyType === "4") && (
                        <Table.TextCell>{formatCurrency(tier.expenses)}</Table.TextCell>
                      )}
                      {royaltyType === "4" && (
                        <Table.TextCell>{formatCurrency(tier.applicable)}</Table.TextCell>
                      )}
                      <Table.TextCell>{tier.rate}%</Table.TextCell>
                      <Table.TextCell>{formatCurrency(tier.credit)}</Table.TextCell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>

              <Pane marginTop={24}>
                <Heading size={400} marginBottom={8}>Royalty Term Definitions</Heading>
                <Pane>
                  <ul>
                    <li><Paragraph marginLeft={10} size={300}><Strong>% of list price:</Strong> % of title's retail price, no matter how much the publisher earns</Paragraph></li>
                    <li><Paragraph marginLeft={10} size={300}><Strong>% of net receipts:</Strong> % of publisher's income for title, not taking into account expenses</Paragraph></li>
                    <li><Paragraph marginLeft={10} size={300}><Strong>% of net profits:</Strong> % of title's sales minus expenses</Paragraph></li>
                    <li><Paragraph marginLeft={10} size={300}><Strong>% of true profits/profit sharing:</Strong> net profits minus publisher's bottom line percentage</Paragraph></li>
                  </ul>
                </Pane>
              </Pane>
            </Card>
          )
        )}
      </Pane>
    </Pane>
  );
};

export default RoyaltyCalculator;
